// interfaces.ts (you can split this out if needed)

import type { LineItem } from "@contexts/CartInfoContext";
import type { Currency } from "@utils/discountUtils";
import type { CouponData } from "@utils/priceUtils";

export type ShipmentRate = {
  serviceName: string;
  serviceCode: string;
  totalCost: number;
  currency: Currency;
  signature: string;
  shipmentCost?: number;
  otherCost?: number;
};

export type ShippingAddressType = {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
};

export interface CartItem {
  sku: string;
  quantity: number;
}

export type AccessoryProduct = {
  SKU: string;
  Name: string;
  Description: string;
  ShortDescription?: string;
  PriceTier1: number;
  Attachments: string[];
  productCategories: string[];
  Length: number;
  Width: number;
  Height: number;
  Weight: number;
  UOM: string;
  WeightUnits: string;
  DimensionsUnits: string;
};

export interface OrganizationProduct {
  status:
    | "paid"
    | "past_due"
    | "pending_certification"
    | "cancelled"
    | "pending"
    | "unpaid"
    | "canceled";
}

export interface OrganizationProducts {
  [sku: string]: OrganizationProduct;
}

export interface FormDefaults {
  // User input data (sent to create_purchase / update_purchase APIs)
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  phoneNumber: string;
  organizationName: string;
  professionalCredentials: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  billingPostalCode: string;
  accessories: Record<string, number>;
  shipmentRate: ShipmentRate | null;
  agreement: boolean;
  stripePaymentMethodId: string;
  items: CartItem[];
  couponId: string;
  promotionCode: string;
  selectedCurrency: "usd" | "cad" | "aud";

  // Data received from APIs
  uuid: string;
  stripeClientSecret: string;
  totalTax: number | undefined;
  shippingOptions: ShipmentRate[];
  redirectUrl: string;
  organizationProducts: OrganizationProducts;

  // Metadata for pricing/display
  accessoryProducts: AccessoryProduct[];
  licenseProducts: LineItem[];
  licenseSKUsToFetch: string[];
  productCategories: string[];
  accessoryCategories: string[];
  couponAmountOff: number;
  couponPercentOff: number;
  couponData?: CouponData;
  accessoryTotalWeight: number;

  // Navigation state (managed by PurchaseFlowContext, stored in form for reactivity)
  currentStep: number;
}

export const formDefaults: FormDefaults = {
  // User input data
  firstName: "",
  lastName: "",
  email: "",
  fullName: "",
  phoneNumber: "",
  organizationName: "",
  professionalCredentials: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
  billingPostalCode: "",
  accessories: {},
  shipmentRate: null,
  agreement: false,
  stripePaymentMethodId: "",
  items: [],
  couponId: "",
  promotionCode: "",
  selectedCurrency: "usd",

  // Data received from APIs
  uuid: "",
  stripeClientSecret: "",
  totalTax: undefined,
  shippingOptions: [],
  redirectUrl: "",
  organizationProducts: {},

  // Metadata for pricing/display
  accessoryProducts: [],
  licenseProducts: [],
  licenseSKUsToFetch: [],
  productCategories: [],
  accessoryCategories: [],
  couponAmountOff: 0,
  couponPercentOff: 0,
  couponData: undefined,
  accessoryTotalWeight: 0,

  // Navigation state (managed by PurchaseFlowContext)
  currentStep: 1,
};
